import { createSlice } from '@reduxjs/toolkit';
import {
  request,
  generateCancelToken,
  cancelRequests,
  isCancel,
  ejectCancelInterceptor,
} from 'modules/Api/HttpClient';
import {
  BASE_TESTS_URL,
  CUSTOM_TESTS_URL,
  SIMULATION_RESULTS_URL,
  SIMULATION_RESULTS_BY_ID_URL,
  CUSTOM_TEST_RESULTS_URL,
  STUDENTS_BY_GROUP_URL,
  GROUPS_URL,
  UNITS_BY_GROUP_URL,
  SCHOOLS_BY_UNIT_URL,
  CLASSES_BY_SCHOOL_URL,
  CUSTOM_TEST_RESULTS_GENERATE_CERTIFICATE_URL,
  CUSTOM_TEST_RESULTS_GENERATE_REPORT_URL,
  SIMULATION_RESULTS_GENERATE_REPORT_URL,
  SIMULATION_RESULTS_EXPORT_URL,
  SIMULATION_RESULTS_STUDENTS_URL,
  SIMULATION_RESULTS_ANALYSIS_URL,
  CUSTOM_TEST_RESULTS_EXPORT_URL,
  ANSWER_SHEETS_EXPORT_URL,
  STUDENT_EXPORT_LIST_BY_NO_TESTS_DONE_URL,
  STUDENTS_BY_NO_TEST_DONE_URL,
  CATEGORIES_URL,
} from 'modules/Api/Routes';
import { formDataFromObj } from 'modules/Api/RequestData';
import { defaultRequest } from 'modules/Utils/DefaultRequest';
import { formatDateToSubmit, isValidISODate } from 'modules/Utils/Date';
import { Selectors as AuthSelectors } from 'modules/Auth/AuthSlice';
import {
  getUTCDefaultStartDate,
  getUTCDefaultEndDate,
} from 'modules/Utils/Date';

const validDateOr = (value, fallback) =>
  isValidISODate(value) ? value : fallback;

const startDate = getUTCDefaultStartDate();
const endDate = getUTCDefaultEndDate();

let cancelToken;

const initialState = {
  loading: false,
  error: '',
  data: {
    page: 1,
    perPage: 10,
    link_answer_sheets: [],
    link_download_csv: '',
    link_download_xls: '',
    link_answer_sheets_csv: '',
    link_answer_sheets_xls: '',
    total: 0,
    testTag: null,
    search: '',
    groupId: '',
    unitId: '',
    schoolId: '',
    classId: '',
    testId: '',
    categoryId: '',
    endDate: endDate,
    startDate: startDate,
    sort: {
      sortType: null,
      sortBy: null,
    },
  },
  groups: {
    data: [],
    loading: false,
  },
  units: {
    data: [],
    loading: false,
  },
  schools: {
    data: [],
    loading: false,
  },
  classes: {
    data: [],
    loading: false,
  },
  tests: {
    data: [],
    selectedGroup: undefined,
    selectedUnit: undefined,
    selectedClass: undefined,
    selectedSchool: undefined,
    selectedTestId: undefined,
    loading: false,
  },
  groupStudents: {
    groupId: '',
    name: '',
    students: [],
    total: 0,
    page: 1,
    perPage: 8,
    search: '',
    showInfo: false,
    align: 'center',
    loading: false,
    error: null,
  },
  studentsNoTestDone: {
    total: 0,
    page: 1,
    perPage: 8,
    search: '',
    students: [],
    loading: false,
    error: null,
  },
  report: null,
  exportFilter: {
    data: {
      groups: {
        data: [],
        loading: false,
      },
      units: {
        data: [],
        loading: false,
      },
      schools: {
        data: [],
        loading: false,
      },
      classes: {
        data: [],
        loading: false,
      },
      tests: {
        data: [],
        loading: false,
      },
    },
    groupId: '',
    unitId: '',
    schoolId: '',
    classId: '',
    testId: '',
    categoryId: '',
    startDate: startDate,
    endDate: endDate,
    extension: 'csv',
    category: '',
    link: '',
    loading: false,
  },
  loadingDownloadStudentList: false,
  categories: {
    data: [],
    loading: false,
  },
  analysis: {
    data: null,
    loading: false,
    error: '',
  },
};

const simulationResultsSlice = createSlice({
  name: 'simulationResults',
  initialState,
  reducers: {
    cancelRequests: () => {
      cancelToken?.cancel();
      cancelRequests();
    },
    cleanState: () => ({ ...initialState }),
    /**
     * indicate that a request is started
     */
    changeExportFilter: (state, action) => {
      state.exportFilter = {
        ...state.exportFilter,
        [action.payload?.param]: action.payload?.value,
      };
    },
    changeSort: (state, action) => {
      const newSort = action.payload;
      state.data.sort.sortType =
        state.data.sort.sortBy === newSort && state.data.sort.sortType === 'ASC'
          ? 'DESC'
          : 'ASC';
      state.data.sort.sortBy = action.payload;
    },
    changePerPage: (state, action) => {
      state.data.perPage = action.payload;
      state.data.page = 1;
    },
    requestSimulationResultsAnalysis: (state) => {
      state.analysis.loading = true;
      state.analysis.error = '';
    },
    receiveSimulationResultsAnalysis: (state, action) => {
      state.analysis.loading = false;
      state.analysis.data = action.payload;
    },
    receiveSimulationResultsAnalysisError: (state, action) => {
      state.analysis.loading = false;
      state.analysis.error = action.payload;
    },
    requestSimulationResults: (state) => {
      state.loading = true;
      state.report = null;
      state.error = '';
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    /**
     * receive a success response
     */
    receiveRequestSuccess: (state) => {
      state.loading = false;
    },
    /**
     * receive a success simulationResults list response
     */
    receiveSimulationBaseResultsList: (state, action) => {
      // The API can answer with an empty body (204/no envelope), so `content`
      // — and therefore the payload — may be undefined.
      const payload = action.payload || {};
      state.loading = false;
      state.data = {
        ...state.data,
        simulationResults: payload.simulate_results || [],
        link_download_csv: payload.link_download_csv,
        link_download_xls: payload.link_download_xls,
        link_answer_sheets_csv: payload.link_answer_sheets_csv,
        link_answer_sheets_xls: payload.link_answer_sheets_xls,
        total: payload.total_items || 0,
      };
      state.tests = {
        ...state.tests,
        selectedTestId: payload.test_id || state.tests.selectedTestId,
      };
    },
    receiveSimulationResultsStudentsList: (state, action) => {
      const payload = action.payload || {};
      state.loading = false;
      state.data = {
        ...state.data,
        simulationResultsStudents: payload.simulation_results_students || [],
        testTag: payload.test_tag || null,
        total: payload.total_items || 0,
      };
    },
    receiveSimulationResultsList: (state, action) => {
      const payload = action.payload || {};
      state.loading = false;
      state.data = {
        ...state.data,
        simulationResults: payload.custom_test_results || [],
        link_answer_sheets: payload.link_answer_sheets,
        link_download_csv: payload.link_download_csv,
        link_download_xls: payload.link_download_xls,
        link_answer_sheets_csv: payload.link_answer_sheets_csv,
        link_answer_sheets_xls: payload.link_answer_sheets_xls,
        total: payload.total_items || 0,
      };
    },
    updateDate: (state, action) => {
      state.groups.currentStartDate = validDateOr(
        action.payload.start_date,
        state.groups.currentStartDate
      );
      state.groups.currentEndDate = validDateOr(
        action.payload.end_date,
        state.groups.currentEndDate
      );
    },
    clearSimulationResultsList: (state) => {
      state.data = {
        ...state.data,
        simulationResults: [],
        simulationResultsStudents: [],
        testTag: null,
        link_answer_sheets: [],
        link_download_csv: '',
        link_download_xls: '',
        link_answer_sheets_csv: '',
        link_answer_sheets_xls: '',
        total: 0,
        page: 1,
        perPage: 10,
        search: '',
        groupId: '',
        unitId: '',
        schoolId: '',
        classId: '',
        testId: '',
        categoryId: '',
        endDate,
        startDate,
        sort: {
          sortType: null,
          sortBy: null,
        },
      };
    },
    resetPage: (state) => {
      state.data.page = 1;
    },
    clearTests: (state) => {
      state.tests = {
        data: [],
        loading: false,
      };
    },
    /**
     * receive an error response
     */
    receiveSimulationResultsError: (state, action) => {
      state.loading = false;
      state.data = {
        ...state.data,
        simulationResults: [],
        simulationResultsStudents: [],
        total: 0,
      };
      state.error = action.payload;
    },
    changeSimulationResultsPage: (state, action) => {
      state.data.page = action.payload;
    },
    changeSimulationResultsSearch: (state, action) => {
      state.data.page = 1;
      state.data.search = action.payload?.search || '';
    },
    changeSimulationResultsFilters: (state, action) => {
      state.data.page = isNaN(action?.payload?.page)
        ? 1
        : Number(action?.payload?.page);
      state.data.perPage = isNaN(action?.payload?.paginates_per)
        ? state.data.perPage
        : Number(action?.payload?.paginates_per);
      state.data.groupId = action?.payload?.group_id || null;
      state.data.unitId = action?.payload?.unit_id || null;
      state.data.schoolId = action?.payload?.school_id || null;
      state.data.classId = action?.payload?.class_id || null;
      state.data.testId = action?.payload?.test_id || null;
      state.data.categoryId = action?.payload?.category_id || null;
      state.data.studentId = action?.payload?.studentId || null;
      state.data.search = action?.payload?.search || '';
      state.data.startDate = validDateOr(
        action.payload?.start_date,
        state.data.startDate
      );
      state.data.endDate = validDateOr(
        action.payload?.end_date,
        state.data.endDate
      );

      state.exportFilter.groupId = parseInt(action?.payload?.group_id) || '';
      state.exportFilter.unitId = parseInt(action?.payload?.unit_id) || '';
      state.exportFilter.schoolId = parseInt(action?.payload?.school_id) || '';
      state.exportFilter.classId = parseInt(action?.payload?.class_id) || '';
      state.exportFilter.testId = parseInt(action?.payload?.test_id) || '';
      state.exportFilter.categoryId =
        parseInt(action?.payload?.category_id) || '';
      state.exportFilter.startDate = state.data.startDate;
      state.exportFilter.endDate = state.data.endDate;
    },
    requestGroups: (state) => {
      state.groups = {
        ...state.groups,
        loading: true,
      };
      state.exportFilter.data.groups = {
        data: [],
        loading: true,
      };
      state.error = '';
    },
    receiveGroupsError: (state, action) => {
      state.groups = {
        ...state.groups,
        loading: false,
      };
      state.exportFilter.data.groups = {
        ...state.exportFilter.data.groups,
        loading: false,
      };
      state.error = action.payload;
    },
    receiveGroupsList: (state, action) => {
      state.groups = {
        ...state.groups,
        loading: false,
        data: action.payload.content.groups,
      };
      state.exportFilter.data.groups = {
        data: action.payload.content.groups,
        loading: false,
      };
    },
    requestBaseTestsBy: (state) => {
      state.tests = {
        ...state.tests,
        data: [],
        loading: true,
      };
      state.exportFilter.data.tests = {
        data: [],
        loading: true,
      };
      state.error = '';
      state.report = null;
    },
    clearError: (state) => {
      state.error = '';
    },
    receiveBaseTestsError: (state, action) => {
      state.tests = {
        ...state.tests,
        loading: false,
        data: [],
      };
      state.exportFilter.data.tests = {
        data: [],
        loading: false,
      };
      state.error = action.payload;
    },
    receiveBaseTestsList: (state, action) => {
      const tests = action.payload?.tests || [];
      state.tests = {
        ...state.tests,
        loading: false,
        data: tests,
      };
      state.exportFilter.data.tests = {
        data: tests,
        loading: false,
      };
    },
    receiveCustomTests: (state, action) => {
      state.tests = {
        ...state.tests,
        loading: false,
        selectedGroup: action.payload.selected.group_id,
        selectedUnit: action.payload.selected.unit_id,
        selectedClass: action.payload.selected.class_id,
        selectedSchool: action.payload.selected.school_id,
        data: action.payload.custom_tests,
      };
      state.exportFilter.data.tests = {
        data: action.payload.custom_tests,
        loading: false,
      };
    },
    requestUnits: (state) => {
      state.units = {
        data: [],
        loading: true,
      };
      state.exportFilter.data.units = {
        data: [],
        loading: true,
      };
      state.error = '';
    },
    receiveUnitsList: (state, action) => {
      state.units = {
        loading: false,
        data: action.payload.content,
      };
      state.exportFilter.data.units = {
        data: action.payload.content,
        loading: false,
      };
    },
    receiveUnitsError: (state, action) => {
      state.units = {
        ...state.units,
        loading: false,
      };
      state.exportFilter.data.units = {
        ...state.exportFilter.data.units,
        loading: false,
      };
      state.error = action.payload;
    },
    requestSchools: (state) => {
      state.schools = {
        data: [],
        loading: true,
      };
      state.exportFilter.data.schools = {
        data: [],
        loading: true,
      };
      state.error = '';
    },
    receiveSchoolsError: (state, action) => {
      state.schools = {
        ...state.schools,
        loading: false,
      };
      state.exportFilter.data.schools = {
        ...state.exportFilter.data.schools,
        loading: false,
      };
      state.error = action.payload;
    },
    receiveSchoolsList: (state, action) => {
      state.schools = {
        ...state.schools,
        loading: false,
        data: action.payload?.content?.schools,
      };
      state.exportFilter.data.schools = {
        data: action.payload.content?.schools,
        loading: false,
      };
    },
    receiveClassesError: (state, action) => {
      state.classes = {
        ...state.classes,
        loading: false,
      };
      state.exportFilter.data.classes = {
        ...state.exportFilter.data.classes,
        loading: false,
      };
      state.error = action.payload;
    },
    requestStudentsNoTestDone: (state) => {
      state.studentsNoTestDone = {
        ...state.studentsNoTestDone,
        loading: true,
        error: null,
      };
    },
    receiveStudentsList: (state, action) => {
      state.groupStudents = {
        ...state.groupStudents,
        groupId: action.payload.id || state.groupStudents.groupId,
        name: action.payload.name || state.groupStudents.name,
        students: action.payload.students,
        total: action.payload.total_items,
        loading: false,
      };
    },
    receiveStudentsNoTestDone: (state, action) => {
      state.studentsNoTestDone = {
        ...state.studentsNoTestDone,
        page: action.payload.page,
        perPage: action.payload.perPage,
        search: action.payload.search,
        students: action.payload.students,
        total: action.payload.total_items,
        loading: false,
      };
    },
    receiveStudentsNoTestDoneError: (state, action) => {
      state.studentsNoTestDone = {
        ...state.studentsNoTestDone,
        error: action.payload,
      };
    },
    changeStudentsNoTestDonePage: (state, action) => {
      state.studentsNoTestDone.page = action.payload;
    },
    changeStudentsNoTestDoneSearch: (state, action) => {
      state.studentsNoTestDone.search = action.payload.search;
    },
    changeStudentsNoTestDonePerPage: (state, action) => {
      state.studentsNoTestDone.perPage = action.payload;
    },
    clearStudentsNoTestDone: (state) => {
      state.studentsNoTestDone = {
        students: [],
        total: 0,
        page: 1,
        perPage: 8,
        search: '',
      };
    },
    updateGroupStudentsSearch: (state, action) => {
      state.groupStudents.search = action.payload.search;
    },
    changeGroupFormPage: (state, action) => {
      state.groupStudents.page = action.payload;
    },
    requestClasses: (state) => {
      state.classes = {
        data: [],
        loading: true,
      };
      state.exportFilter.data.classes = {
        data: [],
        loading: true,
      };
      state.error = '';
    },
    requestStudents: (state) => {
      state.groupStudents.loading = true;
      state.groupStudents.error = null;
    },
    receiveStudentsError: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    receiveClassesList: (state, action) => {
      state.classes = {
        ...state.classes,
        loading: false,
        data: action.payload.content,
      };
      state.exportFilter.data.classes = {
        data: action.payload.content,
        loading: false,
      };
    },
    updateSelected: (state, action) => {
      state.tests = {
        ...state.tests,
        selectedGroup: action.payload?.group || state.tests.selectedGroup,
        selectedUnit: action.payload?.unit || state.tests.selectedUnit,
        selectedClass: action.payload?.class || state.tests.selectedClass,
        selectedSchool: action.payload?.school || state.tests.selectedSchool,
        selectedTestId: action.payload?.testId || state.tests.selectedTestId,
        selectedCategoryId:
          action.payload?.categoryId || state.tests.selectedCategoryId,
      };
    },
    changeResultSearch: (state, action) => {
      state.data.search = action.payload.search;
      state.data.page = 1;
      state.report = null;
    },
    clearStudents: (state) => {
      state.groupStudents.search = '';
      state.groupStudents.page = 1;
    },
    clearFilters: (state) => {
      state.data.search = '';
      state.data.page = 1;
      state.data.simulationResults = [];
      state.units.data = [];
      state.schools.data = [];
      state.classes.data = [];
      state.tests = {
        ...state.tests,
        data: [],
        selectedUnit: undefined,
        selectedClass: undefined,
        selectedSchool: undefined,
        selectedTestId: undefined,
      };
    },
    receiveReport: (state, action) => {
      state.loading = false;
      state.report = action.payload;
    },
    clearReport: (state) => {
      state.report = null;
    },

    requestExportUnits: (state) => {
      state.exportFilter.data.units = {
        ...state.exportFilter.data.units,
        loading: true,
      };

      state.exportFilter.data.schools = {
        data: [],
        loading: false,
      };

      state.exportFilter.data.classes = {
        data: [],
        loading: false,
      };

      state.exportFilter.unitId = '';
      state.exportFilter.schoolId = '';
      state.exportFilter.classId = '';
      state.exportFilter.testId = '';
      state.exportFilter.categoryId = '';
    },

    receiveExportUnitsError: (state, action) => {
      state.exportFilter.data.units = {
        ...state.exportFilter.data.units,
        loading: false,
      };
      state.error = action.payload;
    },

    receiveExportUnitsList: (state, action) => {
      state.exportFilter.data.units = {
        data: action.payload.content,
        loading: false,
      };
    },

    requestExportSchools: (state) => {
      state.exportFilter.data.schools = {
        ...state.exportFilter.data.schools,
        loading: true,
      };

      state.exportFilter.data.classes = {
        data: [],
        loading: false,
      };

      state.exportFilter.schoolId = '';
      state.exportFilter.classId = '';
      state.exportFilter.testId = '';
      state.exportFilter.categoryId = '';
    },

    receiveExportSchoolsError: (state, action) => {
      state.exportFilter.data.schools = {
        ...state.exportFilter.data.schools,
        loading: false,
      };
      state.error = action.payload;
    },

    receiveExportSchoolsList: (state, action) => {
      state.exportFilter.data.schools = {
        data: action.payload?.content?.schools,
        loading: false,
      };
    },

    requestExportClasses: (state) => {
      state.exportFilter.data.classes = {
        ...state.exportFilter.data.classes,
        loading: true,
      };

      state.exportFilter.classId = '';
      state.exportFilter.testId = '';
      state.exportFilter.categoryId = '';
    },

    receiveExportClassesError: (state, action) => {
      state.exportFilter.data.classes = {
        ...state.exportFilter.data.classes,
        loading: false,
      };
      state.error = action.payload;
    },

    receiveExportClassesList: (state, action) => {
      state.exportFilter.data.classes = {
        data: action.payload.content,
        loading: false,
      };
    },

    requestExportCustomTests: (state) => {
      state.exportFilter.data.tests = {
        ...state.exportFilter.data.tests,
        loading: true,
      };

      state.exportFilter.testId = '';
      state.exportFilter.categoryId = '';
    },

    receiveExportCustomTestsError: (state, action) => {
      state.exportFilter.data.tests = {
        ...state.exportFilter.data.tests,
        loading: false,
      };
      state.error = action.payload;
    },

    receiveExportCustomTestsList: (state, action) => {
      state.exportFilter.data.tests = {
        data: action.payload?.custom_tests || [],
        loading: false,
      };
    },

    clearExportTests: (state) => {
      state.exportFilter.data.tests = {
        data: [],
        loading: false,
      };

      state.exportFilter.testId = '';
      state.exportFilter.categoryId = '';
    },

    requestExportLinks: (state) => {
      state.exportFilter.link = '';
      state.exportFilter.loading = true;
    },

    receiveExportLinks: (state, action) => {
      state.exportFilter.loading = false;
      state.exportFilter.link =
        action?.payload?.[`link_download_${state.exportFilter.extension}`];
    },

    receiveExportLinksError: (state, action) => {
      state.exportFilter.loading = false;
      state.error = action.payload;
    },
    requestDownloadStudentList: (state) => {
      state.loadingDownloadStudentList = true;
    },
    receiveDownloadStudentListSuccess: (state) => {
      state.loadingDownloadStudentList = false;
    },
    receiveDownloadStudentListError: (state) => {
      state.loadingDownloadStudentList = false;
    },
    requestCategories: (state) => {
      state.categories = {
        data: [],
        loading: true,
      };
    },
    receiveCategories: (state, action) => {
      state.categories = {
        data: action.payload,
        loading: false,
      };
    },
    receiveCategoriesError: (state, action) => {
      state.categories = {
        ...state.categories,
        loading: false,
      };
      state.error = action.payload;
    },
  },
});

const Actions = simulationResultsSlice.actions;

const Selectors = {
  fetchListData: (state) => state.simulationResults,
  simulationResultsLoading: ({ simulationResults: { loading } }) => ({
    loading,
  }),
};

const getCategoryFilter = async (categoryId, categories, testId) => {
  if (!categoryId || !testId) return {};

  let categoriesData = categories?.data;

  if (categories.loading) {
    try {
      const response = await request({
        method: 'GET',
        url: CATEGORIES_URL,
        params: {
          sort: 'ASC',
          sort_by: 'name',
          test_id: testId,
          with_full_test: true,
        },
      });
      categoriesData = response.data.content.categories;
    } catch {
      return {};
    }
  }

  const category = categoriesData?.find(
    (cat) => Number(cat.id) === Number(categoryId)
  );

  if (!category) return {};

  if (category.tag === 'FULL_TEST') {
    return { category_type: 'FULL_TEST' };
  }

  return { category_id: categoryId };
};

const Async = {
  fetchSimulationResults: () => async (dispatch, getState) => {
    const {
      simulationResults: {
        data: {
          page,
          perPage,
          groupId,
          unitId,
          schoolId,
          classId,
          testId,
          categoryId,
          search,
          startDate,
          endDate,
          sort: { sortType, sortBy },
        },
        categories,
      },
    } = getState();
    const timezone = AuthSelectors.timezone(getState());

    if (!testId) return;

    ejectCancelInterceptor();
    cancelToken?.cancel();
    cancelToken = generateCancelToken();

    let action;

    dispatch(Actions.requestSimulationResults());
    try {
      const categoryFilter = await getCategoryFilter(
        categoryId,
        categories,
        testId
      );

      const response = await request({
        cancelToken: cancelToken.token,
        method: 'GET',
        url: SIMULATION_RESULTS_URL,
        params: {
          search,
          group_id: groupId && groupId !== 'no-group' ? groupId : null,
          no_group: groupId === 'no-group',
          unit_id: unitId,
          school_id: schoolId,
          class_id: classId,
          test_id: testId,
          ...categoryFilter,
          start_date: formatDateToSubmit(startDate, timezone),
          end_date: formatDateToSubmit(endDate, timezone, 'end'),
          page,
          paginates_per: perPage,
          sort: sortType,
          sort_by: sortBy,
        },
      });

      action = Actions.receiveSimulationBaseResultsList(response.data.content);
    } catch (e) {
      if (!isCancel(e)) {
        action = Actions.receiveSimulationResultsError(e.message);
      }
    }

    action && dispatch(action);
  },

  // No cancel token on purpose: `cancelToken` is a single module-level variable shared by the
  // list thunks, so reusing it here would make the Analysis tab and the table cancel each other.
  fetchSimulationResultsAnalysis: () => async (dispatch, getState) => {
    const {
      simulationResults: {
        data: {
          groupId,
          unitId,
          schoolId,
          classId,
          testId,
          categoryId,
          search,
          startDate,
          endDate,
        },
        categories,
      },
    } = getState();
    const timezone = AuthSelectors.timezone(getState());

    if (!testId) return;

    let action;

    dispatch(Actions.requestSimulationResultsAnalysis());
    try {
      const categoryFilter = await getCategoryFilter(
        categoryId,
        categories,
        testId
      );

      const response = await request({
        method: 'GET',
        url: SIMULATION_RESULTS_ANALYSIS_URL,
        params: {
          search,
          group_id: groupId && groupId !== 'no-group' ? groupId : null,
          no_group: groupId === 'no-group',
          unit_id: unitId,
          school_id: schoolId,
          class_id: classId,
          test_id: testId,
          ...categoryFilter,
          start_date: formatDateToSubmit(startDate, timezone),
          end_date: formatDateToSubmit(endDate, timezone, 'end'),
        },
      });

      action = Actions.receiveSimulationResultsAnalysis(response.data.content);
    } catch (e) {
      action = Actions.receiveSimulationResultsAnalysisError(e.message);
    }

    dispatch(action);
  },

  fetchSimulationResultsStudents: () => async (dispatch, getState) => {
    const {
      simulationResults: {
        data: {
          page,
          perPage,
          groupId,
          unitId,
          schoolId,
          classId,
          testId,
          search,
          startDate,
          endDate,
          sort: { sortType, sortBy },
        },
      },
    } = getState();
    const timezone = AuthSelectors.timezone(getState());

    if (!testId) return;

    ejectCancelInterceptor();
    cancelToken?.cancel();
    cancelToken = generateCancelToken();

    let action;

    dispatch(Actions.requestSimulationResults());

    try {
      const response = await request({
        cancelToken: cancelToken.token,
        method: 'GET',
        url: SIMULATION_RESULTS_STUDENTS_URL,
        params: {
          search,
          group_id: groupId && groupId !== 'no-group' ? groupId : null,
          no_group: groupId === 'no-group',
          unit_id: unitId,
          school_id: schoolId,
          class_id: classId,
          test_id: testId,
          start_date: formatDateToSubmit(startDate, timezone),
          end_date: formatDateToSubmit(endDate, timezone, 'end'),
          page,
          paginates_per: perPage,
          sort: sortType,
          sort_by: sortBy,
        },
      });

      action = Actions.receiveSimulationResultsStudentsList(
        response.data.content
      );
    } catch (e) {
      if (!isCancel(e)) {
        action = Actions.receiveSimulationResultsError(e.message);
      }
    }

    action && dispatch(action);
  },

  getSimulationResultsById:
    ({ id, onSuccess, onError }) =>
    async (dispatch) => {
      let action;

      dispatch(Actions.requestSimulationResults());

      try {
        const response = await request({
          method: 'GET',
          url: `${SIMULATION_RESULTS_BY_ID_URL}?id=${id}`,
        });

        action = Actions.receiveRequestSuccess();
        onSuccess(response);
      } catch (e) {
        action = Actions.setLoading(false);
        onError(e);
      }

      dispatch(action);
    },

  fetchBaseTests: () => async (dispatch) => {
    let action;

    dispatch(Actions.requestBaseTestsBy());

    try {
      const response = await request({
        method: 'GET',
        url: BASE_TESTS_URL,
        params: {
          sort: 'ASC',
          sort_by: 'name',
        },
      });

      action = Actions.receiveBaseTestsList(response.data.content);
    } catch (e) {
      action = Actions.receiveBaseTestsError(e.message);
    }

    dispatch(action);
  },

  fetchCustomTests:
    ({ group_id, unit_id, class_id, school_id }) =>
    async (dispatch) => {
      let action;

      dispatch(Actions.requestBaseTestsBy());

      try {
        const response = await request({
          method: 'GET',
          url: CUSTOM_TESTS_URL,
          params: {
            group_id,
            unit_id,
            school_id,
            sort: 'ASC',
            sort_by: 'name',
          },
        });

        const data = {
          ...response.data.content,
          selected: { group_id, unit_id, class_id, school_id },
        };

        action = Actions.receiveCustomTests(data);
      } catch (e) {
        action = Actions.receiveBaseTestsError(e.message);
      }

      dispatch(action);
    },

  fetchCustomTestsSimulationResults: () => async (dispatch, getState) => {
    const {
      simulationResults: {
        data: {
          page,
          perPage,
          groupId,
          unitId,
          schoolId,
          classId,
          testId,
          search,
          startDate,
          endDate,
          sort: { sortType, sortBy },
        },
      },
    } = getState();
    const timezone = AuthSelectors.timezone(getState());

    if (!testId) return;

    ejectCancelInterceptor();
    cancelToken?.cancel();
    cancelToken = generateCancelToken();

    let action;

    dispatch(Actions.requestSimulationResults());

    try {
      const response = await request({
        cancelToken: cancelToken.token,
        method: 'GET',
        url: CUSTOM_TEST_RESULTS_URL,
        params: {
          search,
          group_id: groupId,
          unit_id: unitId,
          school_id: schoolId,
          class_id: classId,
          custom_test_id: testId,
          start_date: formatDateToSubmit(startDate, timezone),
          end_date: formatDateToSubmit(endDate, timezone, 'end'),
          page,
          paginates_per: perPage,
          sort: sortType,
          sort_by: sortBy,
        },
      });

      action = Actions.receiveSimulationResultsList(response.data.content);
    } catch (e) {
      if (!isCancel(e)) {
        action = Actions.receiveSimulationResultsError(e.message);
      }
    }

    action && dispatch(action);
  },

  fetchNoTestDone:
    ({ model, id, search, page } = {}) =>
    async (dispatch, getState) => {
      const {
        simulationResults: {
          studentsNoTestDone: { perPage, page: pageNoTestDone },
        },
      } = getState();

      let action;

      dispatch(Actions.requestStudentsNoTestDone());

      const params = {
        page: page || pageNoTestDone,
        paginates_per: perPage,
        [model]: id,
        search: search || '',
      };
      try {
        const response = await request({
          method: 'GET',
          url: STUDENTS_BY_NO_TEST_DONE_URL,
          params,
        });

        action = Actions.receiveStudentsNoTestDone({
          ...response.data.content,
          page: params.page,
          perPage: params.paginates_per,
          search: params.search,
        });
      } catch (e) {
        action = Actions.receiveStudentsNoTestDoneError(e.message);
      }

      dispatch(action);
    },

  fetchGroupStudents:
    ({ group_id, search } = {}) =>
    async (dispatch, getState) => {
      const {
        simulationResults: {
          groupStudents: {
            page,
            perPage,
            search: searchName,
            groupId,
            name: groupName,
          },
        },
      } = getState();

      let action;

      search && dispatch(Actions.updateGroupStudentsSearch({ search }));
      dispatch(Actions.requestStudents());

      try {
        const response = await request({
          method: 'GET',
          url: STUDENTS_BY_GROUP_URL,
          params: {
            page,
            paginates_per: perPage,
            test_done: true,
            group_id: group_id || groupId,
            search: search || searchName,
          },
        });

        const data = {
          ...response.data.content,
          id: group_id || groupId,
          name: name || groupName,
        };

        action = Actions.receiveStudentsList(data);
      } catch (e) {
        action = Actions.receiveStudentsError(e.message);
      }

      dispatch(action);
    },

  fetchGroupsList: () => async (dispatch) =>
    defaultRequest(
      dispatch,
      `${GROUPS_URL}?page=1&paginates_per=1000`,
      'GET',
      Actions.requestGroups,
      Actions.receiveGroupsList,
      Actions.receiveGroupsError
    ),

  fetchUnitsByGroupList:
    ({ group_id }) =>
    async (dispatch) => {
      defaultRequest(
        dispatch,
        `${UNITS_BY_GROUP_URL}?group_id=${group_id}`,
        'GET',
        Actions.requestUnits,
        Actions.receiveUnitsList,
        Actions.receiveUnitsError
      );
    },

  fetchSchoolsByUnitList:
    ({ unit_id }) =>
    async (dispatch) =>
      defaultRequest(
        dispatch,
        `${SCHOOLS_BY_UNIT_URL}?unit_id=${unit_id}`,
        'GET',
        Actions.requestSchools,
        Actions.receiveSchoolsList,
        Actions.receiveSchoolsError
      ),

  fetchClassesBySchoolList:
    ({ school_id }) =>
    async (dispatch) =>
      defaultRequest(
        dispatch,
        `${CLASSES_BY_SCHOOL_URL}?school_id=${school_id}`,
        'GET',
        Actions.requestClasses,
        Actions.receiveClassesList,
        Actions.receiveClassesError
      ),

  generateCertificatePDF:
    ({ id, studentId, onSuccess, onError }) =>
    async (dispatch) => {
      let action;

      dispatch(Actions.requestSimulationResults());

      try {
        const response = await request({
          method: 'GET',
          url: `${CUSTOM_TEST_RESULTS_GENERATE_CERTIFICATE_URL}/${studentId}/${id}`,
          responseType: 'blob',
        });

        action = Actions.receiveRequestSuccess();
        onSuccess(response);
      } catch (e) {
        action = Actions.setLoading(false);
        onError(e);
      }

      dispatch(action);
    },

  generateELLTReport:
    ({ id, studentId, onSuccess, onError }) =>
    async (dispatch) => {
      let action;

      dispatch(Actions.requestSimulationResults());

      try {
        const response = await request({
          method: 'GET',
          url: `${CUSTOM_TEST_RESULTS_GENERATE_REPORT_URL}/${studentId}/${id}`,
          responseType: 'blob',
        });

        action = Actions.receiveRequestSuccess();
        onSuccess(response);
      } catch (e) {
        action = Actions.setLoading(false);
        onError(e);
      }

      dispatch(action);
    },

  generateReport:
    ({ exercise_id, student_id, onSuccess, onError, isMCER, isPlacement }) =>
    async (dispatch) => {
      let action;
      const url = isMCER
        ? SIMULATION_RESULTS_GENERATE_REPORT_URL.replace('cefr', 'mcer')
        : isPlacement
        ? SIMULATION_RESULTS_GENERATE_REPORT_URL.replace('cefr', 'placement')
        : SIMULATION_RESULTS_GENERATE_REPORT_URL;

      dispatch(Actions.requestSimulationResults());

      try {
        const response = await request({
          method: 'GET',
          url,
          params: {
            exercise_id,
            student_id,
          },
        });

        action = Actions.receiveReport(response.data);
        onSuccess(response.data);
      } catch (e) {
        action = Actions.setLoading(false);
        onError(e);
      }

      dispatch(action);
    },

  fetchExportUnits:
    ({ group_id }) =>
    async (dispatch) =>
      defaultRequest(
        dispatch,
        `${UNITS_BY_GROUP_URL}?group_id=${group_id}`,
        'GET',
        Actions.requestExportUnits,
        Actions.receiveExportUnitsList,
        Actions.receiveExportUnitsError
      ),

  fetchExportSchools:
    ({ unit_id }) =>
    async (dispatch) =>
      defaultRequest(
        dispatch,
        `${SCHOOLS_BY_UNIT_URL}?unit_id=${unit_id}`,
        'GET',
        Actions.requestExportSchools,
        Actions.receiveExportSchoolsList,
        Actions.receiveExportSchoolsError
      ),

  fetchExportClasses:
    ({ school_id }) =>
    async (dispatch) =>
      defaultRequest(
        dispatch,
        `${CLASSES_BY_SCHOOL_URL}?school_id=${school_id}`,
        'GET',
        Actions.requestExportClasses,
        Actions.receiveExportClassesList,
        Actions.receiveExportClassesError
      ),

  fetchExportCustomTests:
    ({ group_id, unit_id, school_id }) =>
    async (dispatch) => {
      let action;

      dispatch(Actions.requestExportCustomTests());

      try {
        const response = await request({
          method: 'GET',
          url: CUSTOM_TESTS_URL,
          params: {
            group_id,
            unit_id,
            school_id,
            sort: 'ASC',
            sort_by: 'name',
          },
        });

        const data = response.data.content;

        action = Actions.receiveExportCustomTestsList(data);
      } catch (e) {
        action = Actions.receiveExportCustomTestsError(e.message);
      }

      dispatch(action);
    },

  fetchExportSubmitBase:
    ({ onSuccess, onError }) =>
    async (dispatch, getState) => {
      const {
        simulationResults: {
          data: { search },
          exportFilter: {
            groupId,
            unitId,
            schoolId,
            classId,
            testId,
            startDate,
            endDate,
            extension,
            show_subcategory_scores,
            show_use_of_english_subcategory_scores,
          },
        },
      } = getState();
      const timezone = AuthSelectors.timezone(getState());
      let action;

      dispatch(Actions.requestExportLinks());

      const data = {
        test_id: testId,
        file_type: extension,
        start_date: formatDateToSubmit(startDate, timezone),
        end_date: formatDateToSubmit(endDate, timezone, 'end'),
        search,
        show_subcategory_scores,
        show_use_of_english_subcategory_scores,
      };

      groupId && (data.group_id = groupId);
      unitId && (data.unit_id = unitId);
      schoolId && (data.school_id = schoolId);
      classId && (data.class_id = classId);

      try {
        const response = await request({
          method: 'POST',
          url: SIMULATION_RESULTS_EXPORT_URL,
          data,
        });

        action = Actions.receiveExportLinks(response.data.content);
        onSuccess(response.data.content);
      } catch (e) {
        action = Actions.receiveExportLinksError(e.message);
        onError();
      }

      dispatch(action);
    },

  fetchExportSubmitCustom:
    ({ isResultsAnswerSheets, onSuccess, onError }) =>
    async (dispatch, getState) => {
      const {
        simulationResults: {
          data: { search },
          exportFilter: {
            groupId,
            unitId,
            schoolId,
            classId,
            testId,
            startDate,
            endDate,
            extension,
            show_subcategory_scores,
            show_use_of_english_subcategory_scores,
          },
        },
      } = getState();
      const timezone = AuthSelectors.timezone(getState());
      let action;

      dispatch(Actions.requestExportLinks());

      const data = {
        custom_test_id: testId,
        file_type: extension,
        start_date: formatDateToSubmit(startDate, timezone),
        end_date: formatDateToSubmit(endDate, timezone, 'end'),
        search,
        show_subcategory_scores,
        show_use_of_english_subcategory_scores,
      };

      groupId && (data.group_id = groupId);
      unitId && (data.unit_id = unitId);
      schoolId && (data.school_id = schoolId);
      classId && (data.class_id = classId);

      try {
        const response = await request({
          method: 'POST',
          url: isResultsAnswerSheets
            ? ANSWER_SHEETS_EXPORT_URL
            : CUSTOM_TEST_RESULTS_EXPORT_URL,
          data,
        });

        action = Actions.receiveExportLinks(response.data.content);
        onSuccess(response.data.content);
      } catch (e) {
        action = Actions.receiveExportLinksError(e.message);
        onError();
      }

      dispatch(action);
    },

  downloadStudentList:
    ({ model, id, onSuccess, onError }) =>
    async (dispatch) => {
      dispatch(Actions.requestDownloadStudentList());
      try {
        const noTestsDoneData = formDataFromObj({
          search: '',
          [model]: id,
          file_type: 'xls',
          show_name: true,
          show_email: true,
          show_group_id: true,
          show_unit_id: true,
          show_school_id: true,
          show_class_id: true,
          show_test_names: true,
          show_sso_token: true,
          show_created_at_formatted: true,
        });

        const response = await request({
          method: 'POST',
          url: STUDENT_EXPORT_LIST_BY_NO_TESTS_DONE_URL,
          data: noTestsDoneData,
        });

        dispatch(Actions.receiveDownloadStudentListSuccess());
        onSuccess(response.data.content);
      } catch (e) {
        dispatch(Actions.receiveDownloadStudentListError());
        onError(e);
      }
    },
  fetchCategories:
    ({ testId, customTestId }) =>
    async (dispatch) => {
      dispatch(Actions.requestCategories());
      try {
        const response = await request({
          method: 'GET',
          url: CATEGORIES_URL,
          params: {
            sort: 'ASC',
            sort_by: 'name',
            test_id: testId ? testId : undefined,
            custom_test_id: customTestId ? customTestId : undefined,
            with_full_test: customTestId ? false : true,
          },
        });
        dispatch(Actions.receiveCategories(response.data.content.categories));
      } catch (e) {
        dispatch(Actions.receiveCategoriesError(e.message));
      }
    },
};

const reducer = simulationResultsSlice.reducer;

export { reducer, Actions, Async, Selectors };
